import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    auctionPrices: Array,
    defaultBaseCommissions: Object,
    savedAuctionPrice: String,
    savedAuctionType: String,
    savedOfferType: String,
    savedBasePrice: Number
  }

  static targets = [
    "agency",
    "auctionPrice",
    "auctionType",
    "basePrice",
    "existingUser",
    "logisticsPricing",
    "newUser",
    "offerType",
    "offerTypeFields",
    "quoteEmail",
    "sellerType",
    "startTime"
  ]

  static outlets = ["admin--quotes--logistics-pricing", "admin--quotes--option"]

  static classes = ["hidden"]

  #offerTypes = []

  connect() {
    this.#offerTypes = [...this.offerTypeTarget.options].map(({ value, text }) => ({ id: value, label: text }))
    this.toggleSellerFields()
    this.toggleAuctionTypeFields()

    if (this.#minBasePrice > this.basePrice) {
      this.updateMinBasePrice()
    }
    this.basePriceTarget.setAttribute("min", this.#minBasePrice)
  }

  adminQuotesLogisticsPricingOutletConnected() {
    this.#updateTravelRate()
  }

  // The travel rate depends on the auction's year only.
  updateYear() {
    this.#updateTravelRate()
    this.#updateMarketingOffers()
  }

  // The agency's country decides which auction prices and marketing packages and services the quote is offered.
  updateCountry() {
    this.refreshAuctionPrices()
    this.adminQuotesOptionOutlets.forEach(option => {
      option.countryChanged()
    })
  }

  userSelected(event) {
    const { email } = event.detail.selected.dataset

    this.#prefillQuoteEmail(email)
  }

  emailUpdated(event) {
    this.#prefillQuoteEmail(event.target.value)
  }

  toggleSellerFields() {
    const existingUserSelected = this.sellerTypeTarget.value === "existing_user"
    const newUserSelected = this.sellerTypeTarget.value === "new_user"

    this.existingUserTarget.classList.toggle(this.hiddenClass, newUserSelected)
    this.existingUserTarget.disabled = newUserSelected

    this.newUserTarget.classList.toggle(this.hiddenClass, existingUserSelected)
    this.newUserTarget.disabled = existingUserSelected
  }

  toggleAuctionTypeFields() {
    this.adminQuotesOptionOutlets.forEach(option => {
      option.auctionTypeChanged()
    })
    this.#toggleOfferTypeFields()
    this.#updateAuctionPrices()
    this.toggleLogisticsPricing()
    this.#updateMarketingOffers()
  }

  // The marketing packages follow the offer type, as the auction prices do.
  refreshAuctionPrices() {
    this.#updateAuctionPrices()
    this.#updateMarketingOffers()

    if (this.#minBasePrice !== parseFloat(this.basePriceTarget.min)) {
      this.updateMinBasePrice()
    }
    this.toggleLogisticsPricing()
  }

  updateMinBasePrice() {
    this.basePriceTarget.value = this.#minBasePrice
    this.basePriceTarget.setAttribute("min", this.#minBasePrice)
    this.updateMaxPriceDiscount()
  }

  updateMaxPriceDiscount() {
    this.adminQuotesOptionOutlets.forEach(option => {
      option.basePriceChanged()
    })
  }

  toggleLogisticsPricing() {
    const isDisabled = !this.#selectedAuctionPrice?.logisticsPricing
    this.logisticsPricingTarget.classList.toggle(this.hiddenClass, isDisabled)
    this.logisticsPricingTarget.disabled = isDisabled
  }

  // outlet API: read by admin--quotes--option via the form outlet
  get basePrice() {
    return parseFloat(this.basePriceTarget.value)
  }

  // outlet API: read by admin--quotes--option via the form outlet
  get year() {
    return this.startTimeTarget.value.slice(0, 4)
  }

  // outlet API: read by admin--quotes--option via the form outlet
  get country() {
    return this.agencyTarget.selectedOptions[0]?.dataset.country ?? ""
  }

  // outlet API: read by admin--quotes--option via the form outlet
  get offerType() {
    return this.offerTypeFieldsTarget.disabled ? "" : this.offerTypeTarget.value
  }

  // outlet API: read by admin--quotes--option via the form outlet
  get defaultBaseCommission() {
    return this.defaultBaseCommissionsValue[this.country]
  }

  // outlet API: read by admin--quotes--option via the form outlet
  get auctionType() {
    return this.auctionTypeTarget.value
  }

  // A saved quote keeps the base price it was quoted at under its own pricing, even below the pricing's current price.
  get #minBasePrice() {
    const price = this.#selectedAuctionPrice?.price ?? 0
    if (this.auctionPriceTarget.value !== this.savedAuctionPriceValue) return price

    return Math.min(price, this.savedBasePriceValue)
  }

  get #selectedAuctionPrice() {
    return this.auctionPricesValue.find(({ id }) => id === this.auctionPriceTarget.value)
  }

  #prefillQuoteEmail(email) {
    this.quoteEmailTarget.value = email
  }

  #toggleOfferTypeFields() {
    const isDisabled = this.auctionType === "event"
    this.offerTypeFieldsTarget.classList.toggle(this.hiddenClass, isDisabled)
    this.offerTypeFieldsTarget.disabled = isDisabled
  }

  // The auction prices of the auction type in the auction's year and the agency's country.
  get #auctionPricesOfAuction() {
    return this.auctionPricesValue.filter(
      ({ year, country, auctionType }) => auctionType === this.auctionType && year === this.year && country === this.country
    )
  }

  get #savedAuctionType() {
    return this.auctionType === this.savedAuctionTypeValue
  }

  // Offers the offer types one of the auction's auction prices is offered for, plus the one a saved quote already has
  // while it keeps its auction type.
  #updateOfferTypes() {
    const offered = this.#auctionPricesOfAuction.flatMap(({ offerTypes }) => offerTypes)
    const keptOfferType = this.#savedAuctionType ? this.savedOfferTypeValue : ""

    this.#replaceOptions(
      this.offerTypeTarget,
      this.#offerTypes.filter(({ id }) => id === "" || id === keptOfferType || offered.includes(id))
    )
  }

  // Offers the auction's auction prices that suit the offer type, plus the auction price a saved quote already has while
  // it keeps its auction type.
  #updateAuctionPrices() {
    this.#updateOfferTypes()

    const offerType = this.offerType
    const savedAuctionType = this.#savedAuctionType
    const auctionPrices = this.auctionPricesValue.filter(
      ({ id, year, country, auctionType, offerTypes }) =>
        (savedAuctionType && id === this.savedAuctionPriceValue) ||
        (auctionType === this.auctionType &&
          year === this.year &&
          country === this.country &&
          (offerType === "" || offerTypes.includes(offerType)))
    )

    this.#replaceOptions(this.auctionPriceTarget, auctionPrices)
  }

  // Keeps the selection where it is still offered.
  #replaceOptions(select, records) {
    const selected = select.value
    select.replaceChildren(...records.map(({ id, label }) => new Option(label, id)))
    select.value = selected
    if (select.selectedIndex === -1) select.selectedIndex = 0
  }

  #updateMarketingOffers() {
    this.adminQuotesOptionOutlets.forEach(option => {
      option.marketingOffersChanged()
    })
  }

  #updateTravelRate() {
    this.adminQuotesLogisticsPricingOutlet.updateYear(this.year)
  }
}
